import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { usePageTitle } from '../../hooks/usePageTitle'
import { RecordFactory } from '../../models/RecordFactory'
import { useHistoryTrackerContext } from '../../providers/HistoryTrackerProvider'
import { useOnboardingContext } from '../../providers/OnboardingProvider'
import { Icon } from '../Icon'
import { PSApplication } from '../app/PSApplication'
import Button from '../app/design/Button'
import Heading from '../app/design/Heading'
import Modal from '../app/design/Modal'
import Paragraph from '../app/design/Paragraph'
import { SelectList } from '../common/SelectList'
import { SetupGratuityModal } from '../settings/SetupGratuityModal'
import { SetupSalesTaxModal } from '../settings/SetupSalesTaxModal'
import { WorkflowPage } from '../workflows/WorkflowPage'

export const SetupOnboardingPaymentsContinuedPage = () => {
    const { logAndDisplayError } = useErrorHandler()
    const { advanceOnboardingState } = useOnboardingContext()
    const navigate = useNavigate()
    usePageTitle('Payments continued')
    const [user, setUser] = useState(
        PSApplication.me().clone({ allow_bnpl: true, allow_surcharge: true, allow_gratuity: true })
    ) // bnpl should be turned on by default

    const { goBack } = useHistoryTrackerContext()

    const [isSaving, setIsSaving] = useState(false)

    const [showEditModal, setShowEditModal] = useState('')

    const paymentOptions = useMemo(
        () => [
            {
                id: 'surcharge',
                title: 'Surcharge (clients pay fee)',
                subtitle: '3% fee added to all payments',
                tertiaryView: <Icon icon="percentage3.large" background="round" />,
                selected: user.allowSurcharge,
            },
            {
                id: 'bnpl',
                title: 'Buy Now Pay Later',
                subtitle: 'Offer 0% interest financing',
                thirdLine: (
                    <Button
                        size="small"
                        intent="tertiaryLink"
                        className="w-fit px-0"
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation()
                            e.preventDefault()
                            setShowEditModal('bnpl')
                        }}
                    >
                        Learn more
                    </Button>
                ),
                tertiaryView: <Icon icon="calendarCheck.large" background="round" />,
                selected: user.allowBNPL,
            },
            {
                id: 'sales-tax',
                title: 'Sales tax',
                subtitle: 'Automatically collect sales tax',
                tertiaryView: <Icon icon="percentage2.large" background="round" />,
                thirdLine: user.allowSalesTax ? (
                    <Paragraph size="sm" className="text-secondary-400">
                        {user.taxcode?.rate ? `${user.taxcode?.rate}% · ${user.taxAddress}` : '-'}
                    </Paragraph>
                ) : undefined,
                selected: user.allowSalesTax,
            },
            {
                id: 'gratuity',
                title: 'Gratuity',
                subtitle: 'Allow clients to leave tips',
                tertiaryView: <Icon icon="money.large" background="round" />,
                thirdLine:
                    user.allowGratuity && user.gratuity ? (
                        <Paragraph size="sm" className="text-secondary-400">
                            {`${user.gratuity}%`}
                        </Paragraph>
                    ) : undefined,
                selected: user.allowGratuity,
            },
            // {
            //     id: 'discounts',
            //     title: 'Discounts',
            //     subtitle: 'Give discounts to clients',
            //     tertiaryView: <Icon icon="discount.large" background="round" />,
            //     selected: false,
            // },
        ],
        [user]
    )

    const paymentOptionsWithEditButtons = useMemo(() => {
        return paymentOptions.map((o) => ({
            ...o,
            button:
                o.selected && !['bnpl', 'surcharge'].includes(o.id)
                    ? {
                          title: 'Edit',
                          onClick: () => {
                              setShowEditModal(o.id)
                          },
                      }
                    : undefined,
        }))
    }, [paymentOptions])

    const onNextHandler = async () => {
        setIsSaving(true)
        const userResult = await RecordFactory.getInstance().sendUser(user, undefined)
        setIsSaving(false)
        if (!userResult.ok) {
            logAndDisplayError(userResult.error)
            return
        }

        await advanceOnboardingState()
    }

    return (
        <>
            <WorkflowPage
                onClose={() => navigate('/')}
                onBack={() => goBack()}
                backButtonTitle={'Back'}
                footerContent={
                    <div className="flex justify-end">
                        <Button intent="primary" onClick={onNextHandler} isLoading={isSaving}>
                            Next
                        </Button>
                    </div>
                }
            >
                <Heading size="xl" className="mt-[5px]">
                    Payments continued
                </Heading>
                <Paragraph className="mt-4 max-w-[580px] text-center text-gray-600">
                    These are specific details for what your clients pay when booking.
                </Paragraph>
                <WorkflowPage.Card className="mt-12">
                    <SelectList
                        selectionType="multi"
                        items={paymentOptionsWithEditButtons}
                        selectedItems={paymentOptions.filter((o) => o.selected)}
                        onSelectItems={(items) => {
                            const selectedIDs = items.map((i) => i.id)

                            setUser(
                                user.clone({
                                    allow_gratuity: selectedIDs.includes('gratuity'),
                                    allow_salestax: selectedIDs.includes('sales-tax'),
                                    allow_bnpl: selectedIDs.includes('bnpl'),
                                    allow_surcharge: selectedIDs.includes('surcharge'),
                                })
                            )
                        }}
                    />
                </WorkflowPage.Card>
            </WorkflowPage>
            <SetupSalesTaxModal
                isOpen={showEditModal === 'sales-tax'}
                onCancel={() => setShowEditModal('')}
                currentData={{
                    location: user.taxAddress,
                    alwaysCollectTax: user.alwaysCollectTax,
                }}
                onDone={(location, taxcode, alwaysCollectTax) => {
                    setUser(
                        user.clone({
                            tax_address: location,
                            taxcode,
                            always_collect_tax: alwaysCollectTax,
                        })
                    )
                    setShowEditModal('')
                }}
            />
            <SetupGratuityModal
                isOpen={showEditModal === 'gratuity'}
                onClose={() => setShowEditModal('')}
                onDone={(gratuity, gratuityPercentage) => {
                    setUser(
                        user.clone({ request_gratuity: gratuity, gratuity: gratuityPercentage })
                    )
                    setShowEditModal('')
                }}
            />
            <Modal
                isOpen={showEditModal === 'bnpl'}
                onClose={() => setShowEditModal('')}
                title="Buy Now Pay Later"
            >
                <div className="flex flex-col gap-y-4">
                    <Paragraph className="text-gray-600">
                        Offer clients “Buy Now Pay Later” (BNPL) 0% interest financing with up to
                        three approved Buy Now Pay later providers, including Affirm, AfterPay, and
                        Klarna. Buy Now Pay Later financing programs are proven to be an effective
                        way to grow your income. BNPL financing options will appear as a payment
                        option when clients are paying for a package, invoice, or product. If your
                        client would like to use Buy Now Pay Later to finance an appointment, class
                        or reservation, you can add that appointment to an invoice and send it to
                        them for payment.
                    </Paragraph>
                    <Paragraph className="text-gray-600">
                        The fees associated with BNPL is 6% + $0.30 per transaction, including your
                        standard PocketSuite processing fee. Surcharge fees do not apply for Buy Now
                        Pay Later transactions, which means this 6% + $0.30 fee cannot be passed on
                        to your client. For a $100 client purchase financed with Buy Now Pay Later,
                        your client will pay $100 and you will receive a payout of $93.70. BNPL
                        payments from your client are paid out to you in the same time frame as
                        standard client credit/debit card payment transactions. Once your client is
                        approved for Buy Now Pay Later, it works just like a credit card or a 0% /
                        low interest loan. Your client (not you) is completely responsible for
                        repayment to the Buy Now Pay Later financing provider.
                    </Paragraph>
                </div>
            </Modal>
        </>
    )
}
